import React, { PropsWithChildren } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  clearNewPatientFormValues,
  closeNewPatientDrawer,
  selectEpicExtPatientIdNewPatientFormValue,
  useCurrentHospitalCountry
} from '@/modules/appContext';
import {
  PendingPatientRequest,
  useCreatePendingPatientMutation
} from '@/modules/pendingPatient';
import { useTranslator, TranslatedText } from '@ianesthesie-kit/core';
import { Box, Grid, Stack, TextField } from '@mui/material';
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikHelpers,
  FormikProps
} from 'formik';
import {
  BirthDateFormDatePicker,
  FormInput,
  FormTelInput,
  GenderFormToggle
} from '@ianesthesie-kit/form';
import isEmpty from 'lodash/fp/isEmpty';
import { useErrorDialog, useSnackbar } from '@ianesthesie-kit/redux';
import {
  getPatientInitialFormValues,
  isTelValidCandidate,
  isValidTel,
  PendingPatientSchema,
  pendingPatientValidation
} from './patient.schema';
import { DeadlineField } from './formComponents';
import { SubmitButton } from '@/components/drawer/components/SubmitButton';
import { ReduxFormValuesListener } from '@/components/drawer/newPatientDrawer/ReduxFormValuesListener';
import { themeColors } from '@ianesthesie-kit/config';
import { styled } from '@mui/material/styles';
import { SurgeonNameAutocomplete } from '@/components/surgeonNameAutocomplete';

const cleanFormValues = (values: PendingPatientSchema) => {
  const fieldsToDelete = (Object.keys(values) as (keyof typeof values)[])
    .filter((k) => k !== 'pendingPatientIdentifier' && k !== 'phoneNumber') // special handling for these
    .filter((k) => isEmpty(values[k]));
  fieldsToDelete.forEach((k) => delete values[k]);

  if (
    isEmpty(values.pendingPatientIdentifier) ||
    (!values.pendingPatientIdentifier?.identifier &&
      !values.pendingPatientIdentifier?.type)
  ) {
    delete values.pendingPatientIdentifier;
  }

  if (isEmpty(values.phoneNumber) || values.phoneNumber.length < 4) {
    delete values.phoneNumber;
  }

  return values;
};

const FieldGroupWrapper = styled(Stack)`
  position: relative;
  padding: 16px 8px 0;
  margin: 32px -16px 8px;

  &:before {
    content: '';
    border-top: 2px solid ${themeColors.slate[200]};
    position: absolute;
    top: -1px;
    left: 0;
    width: calc(100% - 16px);
    height: 1px;
    margin-left: 6px;
  }

  .group-title {
    position: absolute;
    background: ${(p) =>
      (p.theme.components?.MuiDrawer?.styleOverrides?.paper as any)
        ?.backgroundColor};
    top: 0;
    left: 0;
    transform: translate(16px, -60%);
    padding: 0 4px;
    color: ${themeColors.slate[400]};
    font-weight: 600;
    font-size: smaller;
  }
`;

const FieldGroup = ({
  titleId,
  children
}: PropsWithChildren<{
  titleId: 'required' | 'atLeastOneRequired' | 'optional';
}>) => (
  <FieldGroupWrapper>
    <div className="group-title">
      <TranslatedText
        id={`doctor-dashboard.createNewPendingPatient.fieldGroup.${titleId}`}
      />
    </div>
    {children}
  </FieldGroupWrapper>
);

export const PendingPatientForm = () => {
  const translate = useTranslator();
  const dispatch = useAppDispatch();
  const [createPendingPatient, { isLoading }] =
    useCreatePendingPatientMutation();
  const { showSnackbar } = useSnackbar();
  const { showErrorDialog } = useErrorDialog();
  const country = useCurrentHospitalCountry();
  const epicIdExists = !!useAppSelector(
    selectEpicExtPatientIdNewPatientFormValue
  );

  const closeDrawer = () => {
    dispatch(closeNewPatientDrawer());
  };

  async function onSubmit(
    rawValues: PendingPatientSchema,
    helper: FormikHelpers<PendingPatientSchema>
  ) {
    const values = cleanFormValues({ ...rawValues });
    createPendingPatient(values as PendingPatientRequest)
      .unwrap()
      .then(() => {
        closeDrawer();
        setTimeout(() => {
          helper.resetForm();
          dispatch(clearNewPatientFormValues());
          showSnackbar({
            message: translate(
              'doctor-dashboard.createNewPendingPatient.successMessage'
            ),
            severity: 'success'
          });
        });
      })
      .catch(showErrorDialog);
  }

  const canSubmit = (formik: FormikProps<PendingPatientSchema>) => {
    const can =
      !isLoading &&
      formik.isValid &&
      !formik.isSubmitting &&
      !isEmpty(formik.touched);
    if (!can) {
      return false;
    }
    const validTel = isValidTel(formik.values.phoneNumber);
    if (!validTel) {
      return false;
    }
    return (
      (validTel && (formik.values.phoneNumber?.length ?? 0) > 4) ||
      !!formik.values.email
    );
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={pendingPatientValidation}
      initialValues={getPatientInitialFormValues('pendingPatient')}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <>
          <ReduxFormValuesListener />
          <Box sx={{ mt: 1 }}>
            <Form>
              <FieldGroup titleId="required">
                <FormInput
                  fullWidth
                  margin="dense"
                  label={translate('app.user.firstName')}
                  name="firstName"
                  autoComplete="off"
                />

                <FormInput
                  fullWidth
                  margin="dense"
                  label={translate('app.user.lastName')}
                  name="lastName"
                  autoComplete="off"
                />

                <DeadlineField />
              </FieldGroup>

              <FieldGroup titleId="atLeastOneRequired">
                <FormTelInput
                  name="phoneNumber"
                  fast={false}
                  defaultCountry={country}
                  validatePhoneNumber={isTelValidCandidate(
                    formik.values.phoneNumber
                  )}
                  label={translate('app.phoneNumber')}
                  margin="dense"
                />

                <Field
                  name="email"
                  render={({ field }: FieldProps) => (
                    <TextField
                      fullWidth
                      margin="dense"
                      label={translate('app.user.email')}
                      autoComplete="email"
                      error={!!formik.errors.email && !!formik.touched.email}
                      helperText={
                        formik.errors.email &&
                        formik.touched.email &&
                        translate(formik.errors.email)
                      }
                      {...field}
                      onChange={(e) => {
                        formik.setFieldValue(field.name, e.target.value);
                      }}
                    />
                  )}
                />
              </FieldGroup>

              <FieldGroup titleId="optional">
                <GenderFormToggle formControlProps={{ margin: 'dense' }} />

                <BirthDateFormDatePicker
                  formControlProps={{ margin: 'dense' }}
                  applyAgeLimits={false}
                />

                <Field name="surgeonName">
                  {({ field, form }: FieldProps) => (
                    <SurgeonNameAutocomplete
                      label={translate(
                        'doctor-dashboard.details.surgeryInfo.surgeon'
                      )}
                      value={
                        form.values[field.name] ? [form.values[field.name]] : []
                      }
                      onChange={(surgeons) =>
                        form.setFieldValue(field.name, surgeons?.[0])
                      }
                      textFieldProps={{
                        size: 'medium',
                        color: 'primary',
                        margin: 'dense'
                      }}
                    />
                  )}
                </Field>

                <FormInput
                  fullWidth
                  margin="dense"
                  label={translate('app.user.extAppointmentId')}
                  name="appointmentId"
                  autoComplete="off"
                />

                {!epicIdExists && (
                  <Grid container columnSpacing={1}>
                    <Grid item xs={12} md={6}>
                      <FormInput
                        fullWidth
                        fast={false}
                        margin="dense"
                        label={translate('app.identifier')}
                        name="pendingPatientIdentifier.identifier"
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormInput
                        fullWidth
                        fast={false}
                        margin="dense"
                        label={translate('app.identifierType')}
                        name="pendingPatientIdentifier.type"
                        autoComplete="off"
                      />
                    </Grid>
                  </Grid>
                )}
              </FieldGroup>

              <Box
                style={{
                  position: 'relative',
                  margin: '0 -8px 0'
                }}
              >
                <SubmitButton
                  fullWidth
                  sx={{ mt: 2, mb: 2, textTransform: 'none' }}
                  disabled={!canSubmit(formik)}
                >
                  <TranslatedText id="doctor-dashboard.createNewPendingPatient.submitBtn" />
                </SubmitButton>
              </Box>
            </Form>
          </Box>
        </>
      )}
    </Formik>
  );
};
